import React, { useContext, useEffect, useState } from 'react'
import { Formik, Form, Field, useFormikContext } from 'formik'
import FormikInput from '../../../_common/formik/input_v2'
import SelectField from '../../../_common/formik/select'
import Required from '../../../_common/required'
import { FlexCol, FlexInline, FlexRow, HeaderActionButton, Layout } from '../../styledComponents'
import { addressSchema } from 'pageComponents/Checkout2/util/validationSchema'
import { useCheckout2 } from 'setup/CheckoutProviderFields'
import MyContext from 'setup/context'
import { ArrowDownwardSharp, InfoRounded } from '@mui/icons-material'
import { Tooltip } from '@mui/material'

const CopyFromContact = (props) => {
	const {addressType} = props;
    const { setValues, values } = useFormikContext();
    const { userInfo } = useContext(MyContext);
    const { checkoutEditableFields, openEditors } = useCheckout2();

    function copyFromContact() {
        setValues({
            ...values,
            FirstName: checkoutEditableFields.SelectedContact_FirstName,
            LastName: checkoutEditableFields.SelectedContact_LastName,
            Phone: checkoutEditableFields.SelectedContact_Phone,
            PhoneExt: checkoutEditableFields.SelectedContact_PhoneExt
        }, true).then();
    }
    return (!!userInfo && userInfo.isImpersonatorUser &&
        <FlexRow>
            <HeaderActionButton id={`${addressType}-FromContactBtn`} onClick={copyFromContact} disabled={openEditors.contact}>From Contact <ArrowDownwardSharp /></HeaderActionButton>
        </FlexRow>
    );
}

export default function AddressInput(props) {
    const { checkoutInitializeData } = useCheckout2();
    const {
        address,
        addressType,
        hide,
        addressUpdatedCallback
    } = props;

    function save(values) {
        addressUpdatedCallback(values);
        hide();
    }

    const [disabled, setDisabled] = useState(true);

    //Dependent State/Province/Region Field Stuff
    const [selectedCountry, setSelectedCountry] = useState(address.Country);
    const [statesList, setStatesList] = useState((address.Country === 'US' ? checkoutInitializeData.UsStatesTerritories : (address.Country === 'CA' ? checkoutInitializeData.CaProvinces : []))
        .map((c) => { return { value: c.Value, label: c.Label } }));
    const [stateWord, setStateWord] = useState(address.Country === 'US' ? 'State' : (address.Country === 'CA' ? 'Province' : 'Region'));
    const [zipWord, setZipWord] = useState(address.Country === 'US' ? 'Zip Code' : 'Postal Code');
    const countries = checkoutInitializeData.Countries.map((c) => { return { value: c.Value, label: c.Label } });
    useEffect(() => {
        setStatesList((selectedCountry === 'US' ? checkoutInitializeData.UsStatesTerritories : (selectedCountry === 'CA' ? checkoutInitializeData.CaProvinces : []))
            .map((c) => { return { value: c.Value, label: c.Label } }));
        setStateWord(selectedCountry === 'US' ? 'State' : (selectedCountry === 'CA' ? 'Province' : 'Region'));
        setZipWord(selectedCountry === 'US' ? 'Zip Code' : 'Postal Code');
    }, [selectedCountry, checkoutInitializeData])

    function handleValidateFields(values) {
        //Pull out the current value of the country field into a local state, so it can affect other form elements
        if (values.Country !== selectedCountry) {
            setSelectedCountry(values.Country);
        }

        return addressSchema
            .isValid(values)
            .then((valid) => {
                setDisabled(!valid)
            })
    }

    return (<>
        <Formik
            initialValues={address}
            enableReinitialize={false}
            validationSchema={addressSchema}
            validate={handleValidateFields}
            validateOnBlur={true}
            validateOnChange={true}
            validateOnMount={true}
            onSubmit={(values) => save(values)}
        >
            {props => (<FlexCol>
                <CopyFromContact addressType={addressType} />
                <FlexRow>
                    <Form style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }} onSubmit={props.handleSubmit}>
                        <FormikInput
                            label={<>Address Company/Nickname</>}
							automationPrefix={`${addressType}-`}
                            name="Name"
                        />
                        <FormikInput
                            label={<>First Name</>}
							automationPrefix={`${addressType}-`}
                            name="FirstName"
                        />
                        <FormikInput
                            label={<>Last Name</>}
							automationPrefix={`${addressType}-`}
                            name="LastName"
                        />
                        {/* <FormikInput 
                    label={<>Email<Required /></>}
                    name='Email'
                    /> */}
                        <Field
                            name="Country"
                            component={SelectField}
                            options={countries}
                            placeholder="Select a Country"
                            width={250}
                            isSearchable={false}
                            label={<>Country<Required /></>}
							id={`${addressType}-Country`}
                        />
						{addressType === 'shipping' && 
						<FormikInput
                            label={<>Attention Line (optional)</>}
							automationPrefix={`${addressType}-`}
                            name="Attn"
							placeholder='ATTN: Receiving Dept'
							maxLength={50}
                        />}
                        <FormikInput
                            label={<>Address1 (Street)<Required /> <Tooltip arrow
										title={(
											<span style={{ fontSize: '12px', color: 'white', lineHeight: '18px' }}>
												Address Validation Problems? Try this:<br/>
												<ul>
													<li>If your address is on a numbered route/highway, try using "### STATE ROUTE 123" rather than ### Rt. 123 or ### PA-123<br /></li>
													<li>Sometimes, you might have to specify the next larger/containing locality name in the city portion of the address. For example, using Dallas TX, rather than Irving TX.</li>
													<li>Non-address elements (such as subdividions or industrial park names) should go into the ATTN field or Address 2.</li>
													<li>Military bases/restricted access locations may have special addressing requirements/formats.</li>
													<li>Try using the ZIP Code lookup tool on https://usps.com to see how the postal system prefers your address's format</li>
												</ul>
											</span>
										)}>
											<InfoRounded fontSize='0.7rem' color='info' />
										</Tooltip>
									</>}
							automationPrefix={`${addressType}-`}
                            name="Address1"
							maxLength={50}
                        />
                        <FormikInput
                            label={<>Address2 (Suite/Unit/Etc)</>}
							automationPrefix={`${addressType}-`}
                            name="Address2"
							maxLength={50}
                        />
                        <FormikInput
                            label={<>City<Required /></>}
							automationPrefix={`${addressType}-`}
                            name="City"
                        />
                        <FlexInline>
                            {/*If there are states to choose from, show a picker. Otherwise, show a text box. Text box would be for international (non CA/US) addresses input by employee users */}
                            {statesList.length > 0 ? (
                                <Field
                                    name="State"
									automationPrefix={`${addressType}-`}
                                    component={SelectField}
                                    options={statesList}
                                    placeholder={`Select a ${stateWord}`}
                                    label={<>{stateWord}<Required /></>}
									style={{ marginBottom: "7px", width: "200px", padding: "0px" }}
                                    width={200}
                                />) : (
                                <FormikInput
                                    label={<>{stateWord}<Required /></>}
                                    name="State"
									automationPrefix={`${addressType}-`}
                                    width={200}
                                    style={{ marginBottom: 7, width: 200, paddingLeft: "0px" }}
                                />)}
                            <FormikInput
                                label={<>{zipWord}<Required /></>}
								automationPrefix={`${addressType}-`}
                                name="Zip"
                                width={200}
                                style={{ marginBottom: "7px", width: 200 }}
                            />

                        </FlexInline>
                        <FlexInline>
                            <FormikInput
                                label={<>Phone<Required /></>}
								automationPrefix={`${addressType}-`}
                                name="Phone"
                                width={300}
                                style={{ marginBottom: 7, width: 300, paddingLeft: "0px" }}
                            />
                            <FormikInput
                                label={<>Extension</>}
								automationPrefix={`${addressType}-`}
                                name="PhoneExt"
                                width={100}
                                style={{ marginBottom: 7, width: 100, paddingLeft: "0px" }}
                            />
                        </FlexInline>
                        <Layout>
                            {addressType === 'shipping' ?
                                <HeaderActionButton id={`${addressType}-SaveBtn`} type="submit" disabled={disabled}>Save and Validate</HeaderActionButton>
                                :
                                <HeaderActionButton id={`${addressType}-SaveBtn`} type="submit" disabled={disabled}>Save</HeaderActionButton>
                            }
                        </Layout>
                    </Form>
                </FlexRow>
            </FlexCol>)}
        </Formik>
    </>)
}